<template>
  <div>
    <div class="container">
      <div
        class="error-page flex flex-col lg:flex-row items-center justify-center h-screen text-left"
      >
        <div class="text-white mt-10 lg:mt-0 max-w-4xl px-5">
          <div class="intro-x text-2xl mb-6 mt-4">
            Your team is almost ready!
          </div>
          <div class="intro-x text-5xl font-medium">
            Add a ${{ donationMinimum }} donation to activate your team.
          </div>
          <div class="intro-x text-lg mt-4">
            Make your ${{ donationMinimum }} donation to complete your team
            setup and activate your team so others can join. Your team will not
            be visible until this donation is made. Once you have made the
            donation, your team will be active and available for others to join
            and contribute to.
          </div>
          <router-link
            v-if="team.id"
            :to="{
              name: 'donate',
              query: { team: team.id, city: team.eventCity, name: team.name }
            }"
            class="intro-x btn py-3 px-4 text-white border-white dark:border-dark-5 dark:text-gray-300 mt-10"
          >
            Donate ${{ donationMinimum }} to {{ team.name || "your team" }}
          </router-link>
          <button
            type="button"
            class="intro-x btn p-0 block border-0 mt-12 border-b-2 rounded-none border-transparent hover:border-white"
            @click="openLogoutModal"
          >
            Logout and donate later
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showLogoutModal"
      class="logout-overlay flex items-center justify-center px-4"
      @click.self="closeLogoutModal"
    >
      <div class="intro-y box max-w-3xl w-full p-8 text-center">
        <h2 class="text-2xl font-medium mb-4">
          Are you sure you want to logout and donate later?
        </h2>
        <p class="text-base text-gray-700">
          Your team information is safe and ready to go, but your team will not
          be available and active on the AutismOklahoma Walk website for others
          to join or donate to until you make the minimum donation to start your
          team.
        </p>
        <div class="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            type="button"
            class="btn btn-red border-reddark px-6"
            @click="confirmLogout"
          >
            Yes, I will Login and Donate Later
          </button>
          <button
            type="button"
            class="btn btn-outline px-6"
            @click="closeLogoutModal"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref, computed } from "vue";
import { useStore } from "@/store";
import { useRouter } from "vue-router";
import { getTeam } from "@/api/teams";
import { authLogout } from "@/api/auth";
import {
  isActivationFulfilled,
  normalizeDonationMinimum
} from "@/utils/teamEligibility";

export default defineComponent({
  setup() {
    const store = useStore();
    const router = useRouter();
    const team = ref({});
    const showLogoutModal = ref(false);
    const appSettings = computed(
      () => store.getters["main/eventSettings"] || {}
    );
    const donationMinimum = computed(() => {
      return normalizeDonationMinimum(appSettings.value.donationMinimum);
    });

    const resolveTeamId = user => {
      if (!user || !user.team) {
        return null;
      }

      if (typeof user.team === "object" && user.team !== null) {
        return user.team.id;
      }

      return user.team;
    };

    const donateUrl = computed(() => {
      if (!team.value.id) {
        return "";
      }

      return `/frontend/donate/?team=${team.value.id}&city=${team.value.eventCity}&name=${team.value.name}`;
    });

    const loadTeam = async () => {
      const teamId = resolveTeamId(store.state.auth.user);
      if (!teamId) {
        return;
      }

      const teamData = await getTeam(teamId);
      team.value = teamData;

      if (!teamData.active) {
        router.push({ path: "/team-reactivate" });
        return;
      }

      if (isActivationFulfilled(teamData, donationMinimum.value)) {
        router.push({ path: "/" });
      }
    };

    const logout = async () => {
      const refreshToken = store.state.auth.refreshToken;
      await authLogout(refreshToken);
      await store.dispatch("auth/logout");
      router.push({ path: "/login" });
    };

    const openLogoutModal = () => {
      showLogoutModal.value = true;
    };

    const closeLogoutModal = () => {
      showLogoutModal.value = false;
    };

    const confirmLogout = async () => {
      closeLogoutModal();
      await logout();
    };

    onMounted(() => {
      cash("body")
        .removeClass("main")
        .removeClass("login")
        .addClass("error-page");
      loadTeam();
    });

    return {
      donateUrl,
      showLogoutModal,
      openLogoutModal,
      closeLogoutModal,
      confirmLogout,
      logout,
      team,
      donationMinimum
    };
  }
});
</script>
<style scoped>
.logout-overlay {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 50;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}
</style>
